import { useQuery } from "react-query";

import { APICore } from "./api/apiCore";
import { unwrapItem } from "../components/inventory/unwrap";

// =============================================================================
// Counts for the dashboard's Operations, Finance and Customer Service panels: one
// call per sidebar section, the same shape and options as useComplianceCounts. The
// server leaves out the counts the user has no permission for.
// =============================================================================

export const OPERATIONS_COUNTS_KEY = "operations-counts";
export const FINANCE_COUNTS_KEY = "finance-counts";
export const CUSTOMER_SERVICE_COUNTS_KEY = "customer-service-counts";

export interface OperationsCounts {
  /** Requests nobody has answered yet (Sent / InProgress). */
  requests?: number;
  /** Paid offers still waiting to be transferred to the supplier. */
  offerTransfers?: number;
  /** Integration products awaiting moderation. */
  staging?: number;
  /** Inventory submitted for review (first approval). */
  inventoryReview?: number;
  /** Supplier edits to live inventory waiting in change sets. */
  inventoryChanges?: number;
  /** Supplier promotion cards waiting for an admin (new cards and edits to live ones). */
  promotedCards?: number;
}

export interface FinanceCounts {
  /** Withdrawal requests awaiting a decision. */
  withdrawals?: number;
  /** Referrals waiting for an admin to set the percentage and approve. */
  referrals?: number;
  /** Open periods whose month has ended. */
  periodsToClose?: number;
  /** Payment intents still in flight. */
  paymentIntents?: number;
}

export interface CustomerServiceCounts {
  customers?: number;
  customersThisMonth?: number;
  suppliers?: number;
  suppliersLive?: number;
  /** Active agent users. */
  agents?: number;
  agentsOnline?: number;
  /** Tickets still waiting on staff. */
  tickets?: number;
}

const useQueueCounts = <T extends object>(key: string, path: string): T => {
  const api = new APICore();
  const { data } = useQuery(key, () => api.get(path, {}), {
    enabled: api.isUserAuthenticated(),
    // A queue someone else is working drains while this tab is open.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    // A failed count just means an ellipsis on the card.
    retry: false,
  });
  return unwrapItem<T>(data) ?? ({} as T);
};

export const useOperationsCounts = () => useQueueCounts<OperationsCounts>(OPERATIONS_COUNTS_KEY, "/api/operations/counts");
export const useFinanceCounts = () => useQueueCounts<FinanceCounts>(FINANCE_COUNTS_KEY, "/api/finance/counts");
export const useCustomerServiceCounts = () =>
  useQueueCounts<CustomerServiceCounts>(CUSTOMER_SERVICE_COUNTS_KEY, "/api/customer-service/counts");
